import React from "react";
import { Helmet } from "react-helmet-async";
import { localeToHtmlLang, localeToOgLocale } from "../utils/localeRouting";

function SeoHead({
  lang = "en",
  title,
  description,
  canonicalUrl,
  robots = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
  siteName = "LUKKEY",
  type = "website",
  image,
  images = [],
  alternates = [],
  twitterCard = "summary_large_image",
  schema = [],
}) {
  const schemaList = Array.isArray(schema) ? schema.filter(Boolean) : [];
  const seoImages = [
    ...(Array.isArray(images) ? images : []),
    ...(image ? [{ src: image }] : []),
  ].filter(Boolean);
  const normalizedSeoImages = seoImages
    .map((entry) =>
      typeof entry === "string"
        ? { src: entry }
        : {
            src: entry.src || entry.url,
            alt: entry.alt,
            title: entry.title,
            width: entry.width,
            height: entry.height,
            type: entry.type || entry.encodingFormat,
          },
    )
    .filter((entry) => entry.src);
  const primaryImage = normalizedSeoImages[0] || null;
  const htmlLang = localeToHtmlLang(lang);
  const ogLocale = localeToOgLocale(lang);

  return (
    <Helmet>
      <html lang={htmlLang} />
      <title>{title}</title>
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
      {alternates.map((alternate) =>
        alternate?.href && alternate?.hrefLang ? (
          <link
            key={`${alternate.hrefLang}-${alternate.href}`}
            rel="alternate"
            hrefLang={alternate.hrefLang}
            href={alternate.href}
          />
        ) : null
      )}
      {description ? <meta name="description" content={description} /> : null}
      {robots ? <meta name="robots" content={robots} /> : null}
      {robots ? <meta name="googlebot" content={robots} /> : null}
      {robots ? <meta name="bingbot" content={robots} /> : null}
      {title ? <meta property="og:title" content={title} /> : null}
      {description ? (
        <meta property="og:description" content={description} />
      ) : null}
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      {ogLocale ? <meta property="og:locale" content={ogLocale} /> : null}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      {normalizedSeoImages.map((entry, index) => (
        <meta
          key={`og-image-${entry.src}-${index}`}
          property="og:image"
          content={entry.src}
        />
      ))}
      {normalizedSeoImages.map((entry, index) =>
        entry.alt ? (
          <meta
            key={`og-image-alt-${entry.src}-${index}`}
            property="og:image:alt"
            content={entry.alt}
          />
        ) : null
      )}
      {normalizedSeoImages.map((entry, index) =>
        entry.width ? (
          <meta
            key={`og-image-width-${entry.src}-${index}`}
            property="og:image:width"
            content={String(entry.width)}
          />
        ) : null
      )}
      {normalizedSeoImages.map((entry, index) =>
        entry.height ? (
          <meta
            key={`og-image-height-${entry.src}-${index}`}
            property="og:image:height"
            content={String(entry.height)}
          />
        ) : null
      )}
      {normalizedSeoImages.map((entry, index) =>
        entry.type ? (
          <meta
            key={`og-image-type-${entry.src}-${index}`}
            property="og:image:type"
            content={entry.type}
          />
        ) : null
      )}
      {primaryImage?.src ? <link rel="image_src" href={primaryImage.src} /> : null}
      <meta name="twitter:card" content={twitterCard} />
      {title ? <meta name="twitter:title" content={title} /> : null}
      {description ? (
        <meta name="twitter:description" content={description} />
      ) : null}
      {primaryImage?.src ? (
        <meta name="twitter:image" content={primaryImage.src} />
      ) : null}
      {primaryImage?.alt ? (
        <meta name="twitter:image:alt" content={primaryImage.alt} />
      ) : null}
      {schemaList.map((entry, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}

export default SeoHead;
